import { useState } from 'react';
import { Container, Title, Button, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from '@/hooks/api/useCategories';
import { CategoriesTable } from './components/CategoriesTable';
import { CategoryForm } from './components/CategoryForm';
import type { Category } from '@/types/models';
import type { CategoryFormValues } from './schemas/categorySchema';

export function CategoriesPage() {
  const { organizationId } = useOrganization();
  const { data: categories = [], isLoading } = useCategories(organizationId);
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const [formOpened, setFormOpened] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleCreate = () => {
    setSelectedCategory(null);
    setIsEditing(false);
    setFormOpened(true);
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setIsEditing(true);
    setFormOpened(true);
  };

  const handleDelete = (category: Category) => {
    modals.openConfirmModal({
      title: 'Delete Category',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{category.name}</strong>? This action cannot be
          undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteCategory.mutate(category.id);
      },
    });
  };

  const handleSubmit = async (values: CategoryFormValues, keepOpen = false) => {
    if (!organizationId) return;

    if (isEditing && selectedCategory) {
      await updateCategory.mutateAsync({
        id: selectedCategory.id,
        data: values,
      });
    } else {
      await createCategory.mutateAsync({
        ...values,
        organization_id: organizationId,
      });
    }

    if (!keepOpen) {
      setFormOpened(false);
      setSelectedCategory(null);
    }
  };

  return (
    <Container fluid>
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={1}>Categories</Title>
            <Text c="dimmed" size="sm">
              Manage asset categories for your organization
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            Add Category
          </Button>
        </Group>

        <CategoriesTable
          categories={categories}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
        />

        <CategoryForm
          opened={formOpened}
          onClose={() => {
            setFormOpened(false);
            setSelectedCategory(null);
          }}
          onSubmit={handleSubmit}
          category={isEditing ? selectedCategory : null}
          isLoading={createCategory.isPending || updateCategory.isPending}
        />
      </Stack>
    </Container>
  );
}
